import React from 'react';
import { graphql } from 'react-relay';
import { FormattedMessage, defineMessages } from 'react-intl';

import Tag from '@atlaskit/tag';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss';
import Pressable from '@atlaskit/primitives/pressable';
import TagIcon from '@atlaskit/icon/core/tag';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { useShowDetailsPanel } from '@confluence/details-panel/entry-points/useShowDetailsPanel.ts';
import { EditLabelDialogLoader } from '@confluence/labels/entry-points/EditLabelDialogLoader.ts';
import { VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceFailure,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { UNIVERSAL_LABELS_SHORTCUT } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';

import type {
	FooterLabelsQuery,
	FooterLabelsQueryVariables,
} from './__types__/FooterLabelsQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
export const FooterLabels = (): React.JSX.Element | null => {
	const [contentId = ''] = usePageContentId();
	const [spaceKey = ''] = usePageSpaceKey();
	const { showModal } = useDialogs();
	const { showDetailsPanel } = useShowDetailsPanel();

	const labelsShortcutStrings = useShortcutStrings({ shortcut: UNIVERSAL_LABELS_SHORTCUT });
	const { loading, data, error } = useQueryInterop<FooterLabelsQuery, FooterLabelsQueryVariables>(
		graphql`
			query FooterLabelsQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					type
					operations {
						operation
						targetType
					}
					labels(orderBy: { sortField: LABELLING_CREATIONDATE, direction: ASCENDING }) {
						nodes {
							id
							label
							name
							prefix
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
		},
	);

	if (loading) {
		return null;
	}

	if (error) {
		return (
			<>
				<ExperienceFailure name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE} error={error} />
				<ErrorDisplay error={error} />
			</>
		);
	}

	const content = data?.singleContent;

	const hasEditPermissions = Boolean(
		content?.operations?.find(
			(item) => item?.operation === 'update' && item?.targetType === content?.type,
		),
	);

	const labels =
		content?.labels?.nodes?.filter((node) => node?.name !== 'favourite' || node?.prefix !== 'my') ||
		[];

	if (labels.length === 0) {
		if (!hasEditPermissions) {
			// don't show anything if user doesn't have edit permissions
			return (
				<ExperienceSuccess
					name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE}
					attributes={{ hasEditPermissions, labelsCount: labels.length }}
				/>
			);
		}

		return (
			<Tooltip
				content={
					expValEquals('confluence_comments_cohiti_footer_tooltips', 'isEnabled', true) ? (
						<FormattedMessage {...i18n.addLabelTooltip} />
					) : undefined
				}
				shortcut={labelsShortcutStrings}
				position="top"
			>
				<Pressable
					xcss={addLabelsButton}
					onClick={() =>
						showModal(EditLabelDialogLoader, {
							contentId,
							spaceKey,
							source: 'labelsFooter',
						})
					}
				>
					<TagIcon label="" />
					<FormattedMessage {...i18n.addLabels} />
				</Pressable>
				<ExperienceSuccess
					name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE}
					attributes={{ labelsCount: labels.length, hasEditPermissions }}
				/>
			</Tooltip>
		);
	}

	return (
		<Tooltip
			content={
				expValEquals('confluence_comments_cohiti_footer_tooltips', 'isEnabled', true) ? (
					<FormattedMessage {...i18n.manageLabelsTooltip} />
				) : undefined
			}
			position="top"
		>
			<Pressable
				xcss={labelsContainer}
				onClick={() => {
					showDetailsPanel({ highlightLabels: true });
				}}
			>
				{labels
					.slice(0, 2)
					.map((label) =>
						label?.name ? (
							<Tag
								key={label?.id}
								text={label.name}
								migration_fallback="lozenge"
								hasMargin={false}
								isRemovable={false}
							/>
						) : null,
					)}
				{labels.length > 2 && <Inline xcss={additionalLabels}>+{labels.length - 2}</Inline>}
			</Pressable>
			<ExperienceSuccess
				name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE}
				attributes={{ labelsCount: labels.length }}
			/>
		</Tooltip>
	);
};

const labelsContainer = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
	paddingInline: 'space.100',
	paddingBlock: 'space.050',
	height: '32px',
	backgroundColor: 'color.background.neutral.subtle',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const additionalLabels = xcss({
	font: 'font.body.small',
	fontWeight: 'font.weight.bold',
	paddingInline: 'space.050',
});

const addLabelsButton = xcss({
	display: 'flex',
	gap: 'space.100',
	alignItems: 'center',
	paddingInline: 'space.150',
	borderRadius: 'radius.small',
	color: 'color.text.subtle',
	font: 'font.body',
	height: '32px',
	backgroundColor: 'color.background.neutral.subtle',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const i18n = defineMessages({
	addLabels: {
		id: 'labels-footer.addLabels',
		defaultMessage: 'Add labels',
		description: 'Button text to add labels to a page. On click it opens the add labels dialog.',
	},
	addLabelTooltip: {
		id: 'labels-footer.addLabel-tooltip',
		defaultMessage: 'Add label',
		description: 'Tooltip for shorcut visualizer when button opens the add labels dialog',
	},
	manageLabelsTooltip: {
		id: 'labels-footer.manageLabels-tooltip',
		defaultMessage: 'Manage labels',
		description: 'Tooltip used when button opens the sidebar for managing current labels',
	},
});
